import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import type { AuditoriaItem, MembroEquipe, Papel } from '../api/types';
import { useAuth } from '../hooks/useAuth';
import { dateTime } from '../lib/format';
import { Banner, Spinner } from './States';

/** Nome e explicação de cada perfil, iguais em toda a tela. */
export const PERFIS: Record<Papel, { nome: string; resumo: string }> = {
  master: { nome: 'Master', resumo: 'Dono. Acesso total. Definido no Render, não muda pelo painel.' },
  admin: { nome: 'Admin', resumo: 'Acesso total, menos mexer no Master.' },
  dev: { nome: 'Desenvolvedor', resumo: 'Vê todo o painel, sem alterar nada.' },
  client: { nome: 'Cliente', resumo: 'Sem acesso ao painel.' },
};

function Avatar({ url }: { url: string | null }) {
  return url ? <img className="eq-avatar" src={url} alt="" loading="lazy" /> : <span className="eq-avatar vazio" />;
}

/**
 * Usuários da equipe e o registro de alterações.
 *
 * Duas perguntas numa aba: quem pode mexer no painel, e quem mexeu em quê.
 */
export function EquipePanel() {
  const { user, papel, somenteLeitura } = useAuth();
  const [membros, setMembros] = useState<MembroEquipe[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setMembros((await api.adminEquipe()).membros);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui carregar a equipe.');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function mudarPerfil(m: MembroEquipe, novo: Exclude<Papel, 'master'>) {
    if (m.id === null) return;
    const nome = m.personaName ?? m.steamId;
    if (novo === 'client' && !window.confirm(`Tirar ${nome} da equipe? A pessoa perde o acesso ao painel na hora.`)) {
      return;
    }
    setOcupado(true);
    setErro(null);
    setOk(null);
    try {
      await api.adminMudarPerfil(m.id, novo);
      setOk(novo === 'client' ? `${nome} saiu da equipe.` : `${nome} agora é ${PERFIS[novo].nome}.`);
      await carregar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui mudar o perfil.');
    } finally {
      setOcupado(false);
    }
  }

  // Quem pode mudar perfis: Master e Admin. O Master de outra pessoa e o
  // próprio perfil nunca aparecem como editáveis — a API recusa os dois.
  const podeEditar = (m: MembroEquipe) =>
    !somenteLeitura && (papel === 'master' || papel === 'admin') && m.papel !== 'master' && m.id !== user?.id;

  return (
    <div className="panel">
      <h2>Usuários</h2>
      <p className="muted">
        Quem entra no painel e o que cada um pode fazer. Toda alteração no painel fica no registro abaixo, com o nome de
        quem fez.
      </p>

      <div className="eq-perfis">
        {(['master', 'admin', 'dev'] as const).map((p) => (
          <div key={p} className="eq-perfil">
            <span className={`eq-selo eq-${p}`}>{PERFIS[p].nome}</span>
            <span className="dim">{PERFIS[p].resumo}</span>
          </div>
        ))}
      </div>

      {erro && <Banner kind="error">{erro}</Banner>}
      {ok && <Banner kind="ok">{ok}</Banner>}

      <h3>Equipe</h3>
      {!membros ? (
        <Spinner label="Carregando a equipe…" />
      ) : (
        <div className="table-wrap">
          <table className="admin-table cards-no-mobile eq-tabela">
            <thead>
              <tr>
                <th>Usuário</th>
                <th>Perfil</th>
                <th>Último acesso</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {membros.map((m) => (
                <tr key={m.steamId}>
                  <td data-label="Usuário">
                    <div className="eq-usuario">
                      <Avatar url={m.avatarUrl} />
                      <div>
                        <b>{m.personaName ?? 'Ainda não entrou no site'}</b>
                        {m.id === user?.id && <span className="eq-voce">você</span>}
                        <div className="dim eq-steamid">{m.steamId}</div>
                      </div>
                    </div>
                  </td>
                  <td data-label="Perfil">
                    {podeEditar(m) ? (
                      <select
                        className="select"
                        value={m.papel}
                        disabled={ocupado}
                        aria-label={`Perfil de ${m.personaName ?? m.steamId}`}
                        onChange={(e) => void mudarPerfil(m, e.target.value as Exclude<Papel, 'master'>)}
                      >
                        <option value="admin">Admin</option>
                        <option value="dev">Desenvolvedor</option>
                      </select>
                    ) : (
                      <span className={`eq-selo eq-${m.papel}`}>{PERFIS[m.papel].nome}</span>
                    )}
                  </td>
                  <td className="dim" data-label="Último acesso">
                    {m.lastLoginAt ? dateTime(m.lastLoginAt) : '—'}
                  </td>
                  <td>
                    {podeEditar(m) && (
                      <button
                        className="btn ghost small"
                        onClick={() => void mudarPerfil(m, 'client')}
                        disabled={ocupado}
                      >
                        Tirar da equipe
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!somenteLeitura && (papel === 'master' || papel === 'admin') && (
        <AdicionarMembro onAdicionado={carregar} ocupado={ocupado} setOcupado={setOcupado} />
      )}

      <Registro membros={membros ?? []} />
    </div>
  );
}

/** Procura uma conta que já entrou no site e dá um perfil a ela. */
function AdicionarMembro({
  onAdicionado,
  ocupado,
  setOcupado,
}: {
  onAdicionado: () => Promise<void>;
  ocupado: boolean;
  setOcupado: (v: boolean) => void;
}) {
  const [busca, setBusca] = useState('');
  const [achados, setAchados] = useState<MembroEquipe[] | null>(null);
  const [perfil, setPerfil] = useState<'admin' | 'dev'>('dev');
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const termo = busca.trim();
    if (termo.length < 2) {
      setAchados(null);
      return;
    }
    const t = window.setTimeout(() => {
      void api
        .adminCandidatosEquipe(termo)
        .then((r) => setAchados(r.items))
        .catch(() => setAchados([]));
    }, 300);
    return () => window.clearTimeout(t);
  }, [busca]);

  async function adicionar(m: MembroEquipe) {
    if (m.id === null) return;
    setOcupado(true);
    setErro(null);
    try {
      await api.adminMudarPerfil(m.id, perfil);
      setBusca('');
      setAchados(null);
      await onAdicionado();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui adicionar.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div className="eq-adicionar">
      <h3>Adicionar à equipe</h3>
      <p className="dim">
        A pessoa precisa ter entrado no site uma vez com a Steam. Busque pelo apelido ou cole o SteamID (17 dígitos).
      </p>
      <div className="eq-adicionar-campos">
        <input
          id="eq-busca"
          className="input"
          placeholder="Apelido na Steam ou SteamID"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          disabled={ocupado}
        />
        <select
          id="eq-perfil-novo"
          className="select"
          value={perfil}
          onChange={(e) => setPerfil(e.target.value as 'admin' | 'dev')}
          disabled={ocupado}
          aria-label="Perfil a dar"
        >
          <option value="dev">como Desenvolvedor</option>
          <option value="admin">como Admin</option>
        </select>
      </div>
      {erro && <Banner kind="error">{erro}</Banner>}
      {achados && achados.length === 0 && <span className="dim">Nenhuma conta encontrada.</span>}
      {achados && achados.length > 0 && (
        <ul className="eq-achados">
          {achados.map((m) => (
            <li key={m.steamId}>
              <Avatar url={m.avatarUrl} />
              <div>
                <b>{m.personaName ?? m.steamId}</b>
                <div className="dim eq-steamid">{m.steamId}</div>
              </div>
              <button className="btn primary small" onClick={() => void adicionar(m)} disabled={ocupado}>
                Adicionar como {PERFIS[perfil].nome}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Nomes legíveis para as ações que as rotas gravam com nome próprio. */
const ACOES: Record<string, string> = {
  'equipe.perfil': 'Mudou o perfil de um usuário',
  'pricing.tiers.replace': 'Alterou as faixas de comissionamento',
  'client.status': 'Bloqueou ou liberou um cliente',
  'client.ackNameChange': 'Confirmou a troca de nome de um cliente',
  'credentials.apply': 'Aplicou credenciais do Youpin',
  'kits.create': 'Criou um kit',
  'kits.update': 'Alterou um kit',
  'kits.remove': 'Removeu um kit',
  'kits.reorder': 'Reordenou os kits',
};

function nomeDaAcao(acao: string) {
  if (ACOES[acao]) return ACOES[acao];
  const [metodo, rota] = acao.split(' ');
  if (rota) {
    const verbo = metodo === 'DELETE' ? 'Removeu' : metodo === 'POST' ? 'Enviou' : 'Alterou';
    return `${verbo} em ${rota.replace(/^\/admin\//, '')}`;
  }
  return acao;
}

/** O que mudou, em uma linha, para a lista. O detalhe completo abre embaixo. */
function resumoDoDetalhe(acao: string, detalhe: unknown): string | null {
  if (acao === 'equipe.perfil' && detalhe && typeof detalhe === 'object') {
    const d = detalhe as { usuario?: { nome?: string; steamId?: string }; de?: Papel; para?: Papel };
    const quem = d.usuario?.nome ?? d.usuario?.steamId ?? 'usuário';
    return `${quem}: ${d.de ? PERFIS[d.de].nome : '?'} → ${d.para ? PERFIS[d.para].nome : '?'}`;
  }
  return null;
}

function Registro({ membros }: { membros: MembroEquipe[] }) {
  const [items, setItems] = useState<AuditoriaItem[] | null>(null);
  const [proximo, setProximo] = useState<number | null>(null);
  const [usuario, setUsuario] = useState('');
  const [busca, setBusca] = useState('');
  const [aberto, setAberto] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const buscar = useCallback(
    async (antes?: number) => {
      try {
        const r = await api.adminAuditoria({
          usuario: usuario ? Number(usuario) : undefined,
          busca: busca.trim() || undefined,
          antes,
        });
        setItems((prev) => (antes && prev ? [...prev, ...r.items] : r.items));
        setProximo(r.proximo);
        setErro(null);
      } catch (e) {
        setErro(e instanceof ApiError ? e.message : 'Não consegui carregar o registro.');
      }
    },
    [usuario, busca],
  );

  useEffect(() => {
    const t = window.setTimeout(() => void buscar(), 300);
    return () => window.clearTimeout(t);
  }, [buscar]);

  return (
    <section className="eq-registro">
      <h3>Registro de alterações</h3>
      <div className="eq-filtros">
        <select
          id="eq-filtro-usuario"
          className="select"
          value={usuario}
          onChange={(e) => setUsuario(e.target.value)}
          aria-label="Filtrar por usuário"
        >
          <option value="">Todos da equipe</option>
          {membros
            .filter((m) => m.id !== null)
            .map((m) => (
              <option key={m.id} value={m.id!}>
                {m.personaName ?? m.steamId} · {PERFIS[m.papel].nome}
              </option>
            ))}
        </select>
        <input
          id="eq-filtro-busca"
          className="input"
          placeholder="Buscar: kits, pedido, faixa…"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </div>

      {erro && <Banner kind="error">{erro}</Banner>}
      {!items ? (
        <Spinner label="Carregando o registro…" />
      ) : items.length === 0 ? (
        <p className="muted">Nenhuma alteração registrada com esse filtro.</p>
      ) : (
        <ol className="eq-linhas">
          {items.map((it) => {
            const resumo = resumoDoDetalhe(it.acao, it.detalhe);
            const papel = (it.papel ?? 'client') as Papel | 'token';
            return (
              <li key={it.id}>
                <button
                  type="button"
                  className="eq-linha"
                  aria-expanded={aberto === it.id}
                  onClick={() => setAberto(aberto === it.id ? null : it.id)}
                >
                  <span className="eq-quando dim">{dateTime(it.em)}</span>
                  <span className="eq-quem">
                    <Avatar url={it.usuario?.avatarUrl ?? null} />
                    <b>{it.usuario?.personaName ?? it.usuario?.steamId ?? (papel === 'token' ? 'Token da API' : '—')}</b>
                    {papel !== 'token' && papel !== 'client' && (
                      <span className={`eq-selo eq-${papel}`}>{PERFIS[papel].nome}</span>
                    )}
                  </span>
                  <span className="eq-oque">
                    {nomeDaAcao(it.acao)}
                    {resumo && <span className="dim"> · {resumo}</span>}
                  </span>
                </button>
                {aberto === it.id && (
                  <pre className="eq-detalhe">{JSON.stringify(it.detalhe, null, 2)}</pre>
                )}
              </li>
            );
          })}
        </ol>
      )}
      {proximo !== null && (
        <button className="btn ghost" onClick={() => void buscar(proximo)}>
          Ver mais antigas
        </button>
      )}
    </section>
  );
}
